import type { Command } from 'commander';
import { randomUUID } from 'node:crypto';
import { unwrapDataResponse } from '@wport/core';
import { enterprisePost } from '../../../lib/enterprise-client';
import { resolveApiKey } from '../../../lib/credentials-store';
import { resolveContext } from '../../../lib/global-opts';
import { readJsonObject } from '../../../lib/io-helpers';
import { printJson } from '../../../lib/output';
import type { WriteCtx } from './write-shared';

interface CreateFlags {
	file?: string;
	idempotencyKey?: string;
}

/** create/update 回應：後端只回 { enc_id }（enterprise-jobs.controller）。 */
export interface CreatedJob {
	enc_id?: string;
	[k: string]: unknown;
}

/**
 * `jobs create` 核心：讀 --file/stdin 的 JSON body → POST /jobs（201）→ 回 { enc_id }。
 * body 直送後端驗證（欄位/巢狀 salary·work_area 皆由 server DTO 把關）。
 * idempotencyKey 由呼叫端決定（後端強制帶，缺 → 400）。
 */
export async function runJobsCreate(
	ctx: WriteCtx,
	apiKey: string,
	source: string,
	idempotencyKey: string
): Promise<void> {
	const jobBody = readJsonObject(source, { timeoutMs: ctx.timeoutMs });
	const { body } = await enterprisePost(
		{ baseUrl: ctx.baseUrl, locale: ctx.locale, timeoutMs: ctx.timeoutMs, apiKey },
		'/jobs',
		jobBody,
		{ idempotencyKey }
	);
	const created = unwrapDataResponse<CreatedJob>(body);
	if (ctx.format === 'json') {
		printJson(created);
		return;
	}
	process.stdout.write(`Created job: ${created.enc_id ?? ''}\n`);
}

export function registerEnterpriseJobsCreate(parent: Command): void {
	parent
		.command('create')
		.description('Create a job posting from a JSON file (use "-" to read stdin)')
		.requiredOption('--file <path>', 'path to a JSON job body, or "-" for stdin')
		.option('--idempotency-key <key>', 'reuse across retries to avoid duplicate creates (default: a fresh UUID)')
		.action(async (flags: CreateFlags, command: Command) => {
			const ctx = resolveContext(command);
			const globals = command.optsWithGlobals() as { apiKey?: string };
			const { key } = resolveApiKey(globals.apiKey);
			await runJobsCreate(ctx, key, flags.file as string, flags.idempotencyKey ?? randomUUID());
		});
}
